
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { MapPin, Timer } from "lucide-react";
import { StudentAuthDialog } from "./StudentAuthDialog";
import { StudentRequestDialog } from "./StudentRequestDialog";
import { useTranslations } from "@/hooks/useTranslations";
import { useLanguage } from "@/components/LanguageContext";

// Define training type translations
const trainingTypeTranslations: Record<string, Record<string, string>> = {
  en: {
    "Strength Training": "Strength Training",
    "Functional Training": "Functional Training",
    "HIIT": "HIIT",
    "Cardio": "Cardio",
    "Weight Loss": "Weight Loss",
    "Muscle Gain": "Muscle Gain",
    "Endurance": "Endurance",
    "Flexibility": "Flexibility",
    "Powerlifting": "Powerlifting",
    "Bodybuilding": "Bodybuilding",
    "CrossFit": "CrossFit",
    "Yoga": "Yoga",
    "Pilates": "Pilates",
    "Calisthenics": "Calisthenics"
  },
  pt: {
    "Strength Training": "Musculação",
    "Functional Training": "Treino Funcional",
    "HIIT": "HIIT",
    "Cardio": "Cardio",
    "Weight Loss": "Perda de Peso",
    "Muscle Gain": "Ganho de Massa Muscular",
    "Endurance": "Resistência",
    "Flexibility": "Flexibilidade",
    "Powerlifting": "Powerlifting",
    "Bodybuilding": "Culturismo",
    "CrossFit": "CrossFit",
    "Yoga": "Yoga",
    "Pilates": "Pilates",
    "Calisthenics": "Calistenia"
  }
};

interface ProfileHeaderProps {
  profile: any;
}

export const ProfileHeader = ({ profile }: ProfileHeaderProps) => {
  const { t } = useTranslations("publicProfile");
  const { language } = useLanguage();
  
  // Get the appropriate translation map based on current language
  const translations = trainingTypeTranslations[language as 'en' | 'pt'] || trainingTypeTranslations.en;
  
  return (
    <Card className="border-none shadow-lg bg-white/80 backdrop-blur-sm overflow-hidden">
      {profile.cover_photo_url && (
        <div className="w-full h-36 sm:h-48 overflow-hidden relative">
          <img 
            src={profile.cover_photo_url || "https://images.unsplash.com/photo-1605296867304-46d5465a13f1?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80"} 
            alt="Capa" 
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/10 to-black/30"></div>
          
          <div className="absolute top-4 left-4">
            <img 
              src="/lovable-uploads/e79feb48-6e7c-488a-8626-bb79f4bde67a.png" 
              alt="TrainerFOLIO Logo" 
              className="h-10 w-auto object-contain"
            />
          </div>
        </div>
      )}
      
      <CardContent className={`p-6 ${!profile.cover_photo_url ? 'pt-4' : '-mt-16'}`}>
        {!profile.cover_photo_url && (
          <div className="flex justify-start mb-6">
            <img 
              src="/lovable-uploads/e79feb48-6e7c-488a-8626-bb79f4bde67a.png" 
              alt="TrainerFOLIO Logo" 
              className="h-10 w-auto object-contain"
            />
          </div>
        )}
        
        <div className="flex flex-col sm:flex-row items-center gap-6">
          <Avatar className={`h-32 w-32 border-4 border-white shadow-lg ${profile.cover_photo_url ? 'ring-4 ring-gray-200' : ''}`}>
            <AvatarImage src={profile.avatar_url} alt={`${profile.first_name} ${profile.last_name}`} />
            <AvatarFallback className="bg-gray-200 text-gray-700">
              {profile.first_name?.[0]}{profile.last_name?.[0]}
            </AvatarFallback>
          </Avatar>
          
          <div className="text-center sm:text-left flex-1">
            <h1 className="text-3xl font-bold mb-3 text-gray-900">
              {profile.first_name} {profile.last_name}
            </h1>
            
            <div className="flex flex-wrap gap-2 justify-center sm:justify-start mb-4">
              {profile.training_types?.map((type: string) => (
                <Badge 
                  key={type} 
                  variant="secondary"
                  className="bg-gray-200 text-gray-700 hover:bg-gray-300 px-3 py-1 rounded-full font-medium"
                >
                  {translations[type] || type}
                </Badge>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 mb-6">
              {profile.experience_years && (
                <div className="flex items-center justify-center sm:justify-start gap-2 text-muted-foreground">
                  <Timer className="h-4 w-4 text-gray-700" />
                  <span>{profile.experience_years} {t("yearsExperience")}</span>
                </div>
              )}
              
              {profile.location && (
                <div className="flex items-center justify-center sm:justify-start gap-2 text-muted-foreground">
                  <MapPin className="h-4 w-4 text-gray-700" />
                  <span>{profile.location}</span>
                </div>
              )}
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-3 justify-center sm:justify-start w-full">
              <div className="w-full sm:w-auto">
                <StudentRequestDialog profile={profile} />
              </div>
              <div className="w-full sm:w-auto">
                <StudentAuthDialog profile={profile} />
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
